import {FormFieldT, FormStepT} from '@/lib/types/form.types';
import {speculatorUpdateT} from '@/lib/types/speculator.types';

import {BANNED_WORDS} from '@/lib/constants/bannedWords';
import {validateTextInput} from '@/lib/helpers/form';
import {LANGUAGES} from '@/lib/jsonData/languageData';
import {formattedTimeZones} from '@/lib/jsonData/timezoneData';
import {US_STATES_DATA} from '@/lib/jsonData/usStatesData';

/****************************************************************************
 * Form Fields
 ****************************************************************************/
export const speculatorFormFields: FormFieldT<speculatorUpdateT>[] = [
  {
    label: 'Email',
    name: 'speculator_email',
    disabled: true,
    options: {
      required: 'Email is required',
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Invalid email address',
      },
    },
  },
  {
    label: 'First Name',
    name: 'speculator_name_first',
    options: {
      required: 'First name is required',
      validate: (value: string) => validateTextInput(value, 'First Name'),
    },
  },
  {
    label: 'Middle Name',
    name: 'speculator_name_middle',
    options: {
      validate: (value: string) => {
        if (value) return validateTextInput(value, 'Middle Name', 1);
        return true;
      },
    },
  },
  {
    label: 'Last Name',
    name: 'speculator_name_last',
    options: {
      required: 'Last name is required',
      validate: (value: string) => validateTextInput(value, 'Last Name'),
    },
  },
  {
    label: 'Username',
    name: 'speculator_username',
    options: {
      required: 'Username is required',
      // Convert to string
      validate: value => {
        if (BANNED_WORDS.includes(value.trim().toLowerCase())) return 'Invalid username';
        // Username cannot contain special characters or spaces
        if (!/^[a-zA-Z0-9-]+$/.test(value))
          return 'Username can only contain letters, numbers, and hyphens (no spaces, underscores, or special characters)';
        return true;
      },
    },
  },
  {
    label: 'Phone Number',
    placeholder: 'Phone Number ex: +1-805-555-1212',
    type: 'tel',
    name: 'speculator_phone',
    options: {
      required: 'Phone number is required',
      pattern: {
        // Supports US and international phone numbers with or without country code
        value: /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
        message: 'Invalid phone number',
      },
    },
  },
  {
    label: 'Address 1',
    name: 'speculator_address',
    options: {required: 'Address is required'},
  },
  {label: 'Address 2', name: 'speculator_address2'},
  {label: 'City', name: 'speculator_city', options: {required: 'City is required'}},
  {
    label: 'State',
    type: 'select',
    name: 'speculator_state',
    placeholder: 'Select State',
    selectOptions: US_STATES_DATA,
    options: {required: 'State is required', validate: value => !!US_STATES_DATA.find(state => state.value === value)},
  },
  {
    label: 'Postal Code',
    name: 'speculator_postal_code',
    options: {
      required: 'Postal code is required',
      pattern: {value: /^\d{5}$/, message: 'Invalid postal code'},
    },
  },
  {
    label: 'Date of Birth',
    type: 'date',
    name: 'speculator_dob',
    options: {
      required: 'Date of birth is required',
      // Must be 18 years or older
      validate: value => {
        const dateOfBirth = new Date(value);
        const currentDate = new Date();
        const monthDiff = currentDate.getMonth() - dateOfBirth.getMonth();
        let age = currentDate.getFullYear() - dateOfBirth.getFullYear();

        if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < dateOfBirth.getDate())) {
          age--;
        }

        if (age < 18) {
          return 'Sorry, you must be 18 years or older';
        }

        return true;
      },
    },
  },
  {
    label: 'Preferred Language',
    type: 'select',
    name: 'speculator_preferred_language',
    placeholder: 'Select Language',
    selectOptions: LANGUAGES,
    options: {
      required: 'Preferred language is required',
      validate: value => !!LANGUAGES.find(language => language.value === value),
    },
  },
  {
    label: 'Time Zone',
    type: 'select',
    name: 'speculator_timezone',
    placeholder: 'Select Time Zone',
    selectOptions: formattedTimeZones,
    options: {
      required: 'Time zone is required',
      validate: value => !!formattedTimeZones.find(zone => zone.value === value),
    },
  },
];

/****************************************************************************
 * Form Steps
 ****************************************************************************/
export type SpeculatorFieldNamesT = (typeof speculatorFormFields)[number]['name'];

export const speculatorFormSteps: FormStepT<SpeculatorFieldNamesT> = [
  {
    step: 0,
    name: 'personal',
    label: 'Personal Information',
    fields: [
      'speculator_email',
      'speculator_name_first',
      'speculator_name_middle',
      'speculator_name_last',
      'speculator_username',
      'speculator_phone',
      'speculator_dob',
    ],
  },
  {
    step: 1,
    name: 'address',
    label: 'Address Information',
    fields: [
      'speculator_address',
      'speculator_address2',
      'speculator_city',
      'speculator_state',
      'speculator_postal_code',
      'speculator_country',
    ],
  },
  {
    step: 2,
    name: 'preferences',
    label: 'Preferences',
    fields: ['speculator_preferred_language', 'speculator_timezone'],
  },
];
